import { useMemo, useRef, useState } from "react"
import { useController, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FormError } from "@/components/ui/form"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchItem, SearchSelectInput, SuggestionSearch } from "@/components/ui/search"
import { useClickOutside } from "@/hooks/useClickOutside"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import {
  PartnerUser,
  TeleconsultationForm,
} from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { SearchOption } from "@/modules/patients/components/search-option"

const formatPartnerAddress = (partner: PartnerUser | null) => {
  if (!partner) return null
  const { streetAddress, postalCode, city, countryCode } = partner.careProvider ?? {}
  return joinStrings([streetAddress, joinStrings([postalCode, city], " "), countryCode], ", ") || null
}

const MedgatePartnerSelect = ({
  partners,
  onPartnerChange,
}: {
  partners: PartnerUser[]
  onPartnerChange?: (partner: PartnerUser | null) => void
}) => {
  const { t } = useTranslation()
  const {
    control,
    formState: { errors },
  } = useFormContext<TeleconsultationForm>()
  const { field } = useController({ name: "assignedPartner", control })

  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)
  const [searchResultVisible, setSearchResultVisible] = useState(false)
  const initialPartner = partners.find((p) => p.email === field.value)
  const [searchText, setSearchText] = useState(() => initialPartner?.userName ?? "")

  const filteredPartners = useMemo(() => {
    if (searchText.length < 3) return partners
    const searchTerms = searchText.trim().toLowerCase().split(" ")
    return partners.filter((partner) => {
      const name = partner.userName.toLowerCase()
      const email = (partner.email ?? "").toLowerCase()
      return searchTerms.some((term) => name.includes(term) || email.includes(term))
    })
  }, [partners, searchText])

  const handleSelect = (partnerId: string) => {
    const partner = partners.find((p) => p.id === partnerId)
    if (partner) {
      field.onChange(partner.email)
      setSearchText(partner.userName)
      onPartnerChange?.(partner)
    }
    setSearchResultVisible(false)
  }

  const handleClear = () => {
    setSearchText("")
    field.onChange(null)
    onPartnerChange?.(null)
  }

  useClickOutside((event: MouseEvent) => {
    if (
      searchResultVisible &&
      searchResultRef.current &&
      event.target instanceof HTMLElement &&
      !searchResultRef.current.contains(event.target)
    ) {
      setSearchResultVisible(false)
    }
  }, searchRef)

  return (
    <div id="assignedPartner">
      <SuggestionSearch>
        <SearchSelectInput
          ref={searchRef}
          required
          label={t("patient.agenda.medgatePartner")}
          value={searchText}
          onChange={({ target: { value } }) => {
            if (value.length === 0) {
              handleClear()
              return
            }
            setSearchText(value)
          }}
          onClear={handleClear}
          onFocus={() => setSearchResultVisible(true)}
          error={!!errors.assignedPartner}
          testId="medgate-partner"
        />
        <SearchContent ref={searchResultRef} className={cn("px-0", searchResultVisible ? "show" : "hidden")}>
          <ScrollArea>
            <div className="py-2" data-testid="medgate-partner-search-section">
              {filteredPartners.length === 0 ? (
                <SearchItem
                  disabled
                  className="text-on-surface mx-2 cursor-default overflow-hidden rounded-md py-1.5"
                  onSelect={() => setSearchResultVisible(false)}>
                  {t("common.nothingFound")}
                </SearchItem>
              ) : (
                filteredPartners.map((partner) => (
                  <SearchOption
                    key={partner.id}
                    searchValue={searchText}
                    value={partner.id}
                    title={partner.userName}
                    synonym={`(${partner.email})`}
                    onSelect={() => handleSelect(partner.id)}
                    className="mx-2"
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </SearchContent>
      </SuggestionSearch>
      {errors.assignedPartner?.message && <FormError>{t(errors.assignedPartner.message)}</FormError>}
    </div>
  )
}

export { MedgatePartnerSelect, formatPartnerAddress }
